import { INTEGRATION_TABS } from "constants/routes";
import type { Datasource } from "entities/Datasource";
import { getDatasources, getPlugins } from "ee/selectors/entitiesSelector";
import { useSelector } from "react-redux";
import type { EventLocation } from "ee/utils/analyticsUtilTypes";
import history from "utils/history";
import { integrationEditorURL } from "ee/RouteBuilder";
import type { AppState } from "ee/reducers";
import { getCurrentAppWorkspace } from "ee/selectors/selectedWorkspaceSelectors";
import { useFeatureFlag } from "utils/hooks/useFeatureFlag";
import { FEATURE_FLAG } from "ee/entities/FeatureFlag";
import {
  getHasCreateDatasourceActionPermission,
  getHasCreateDatasourcePermission,
} from "ee/utils/BusinessFeatures/permissionPageHelpers";
import {
  type ActionOperation,
  createQueryOption,
  generateCreateQueryForDSOption,
  generateCreateNewDSOption,
} from "components/editorComponents/GlobalSearch/utils";
import { getCurrentPackage } from "ee/selectors/packageSelectors";
import { hasCreateModulePermission } from "ee/utils/permissionHelpers";
import {
  getSortedDatasources,
  updateActionOperations,
  useRecentlyUsedDSMap,
} from "components/editorComponents/GlobalSearch/GlobalSearchHooks";
import { actionOperations, appsmithAIActionOperation } from "./utils";
import type { Plugin } from "entities/Plugin";
import { createQueryModule } from "ee/actions/moduleActions";
import { MODULE_TYPE } from "ee/constants/ModuleConstants";

export const useFilteredFileOperations = (query = "") => {
  const allDatasources = useSelector(getDatasources);
  const plugins = useSelector(getPlugins);
  const showAppsmithAIQuery = useFeatureFlag(FEATURE_FLAG.ab_appsmith_ai_query);

  // helper map for sorting based on recent usage
  const recentlyUsedDSMap = useRecentlyUsedDSMap();

  const userPackagePermissions = useSelector(
    (state: AppState) => getCurrentPackage(state)?.userPermissions ?? [],
  );

  const userWorkspacePermissions = useSelector(
    (state: AppState) => getCurrentAppWorkspace(state).userPermissions ?? [],
  );

  const isFeatureEnabled = useFeatureFlag(FEATURE_FLAG.license_gac_enabled);

  const canCreateModules = hasCreateModulePermission(userPackagePermissions);

  const canCreateDatasource = getHasCreateDatasourcePermission(
    isFeatureEnabled,
    userWorkspacePermissions,
  );

  // get all datasources, app ds listed first
  const allDatasourcesWithCreateActionPerm = allDatasources.filter((ds) =>
    getHasCreateDatasourceActionPermission(
      isFeatureEnabled,
      ds.userPermissions ?? [],
    ),
  );

  return useFilteredAndSortedFileOperations({
    allDatasources: allDatasourcesWithCreateActionPerm,
    canCreateModules,
    canCreateDatasource,
    plugins,
    query,
    recentlyUsedDSMap,
    showAppsmithAIQuery,
  });
};

export const useFilteredAndSortedFileOperations = ({
  allDatasources = [],
  canCreateDatasource = true,
  canCreateModules = true,
  plugins = [],
  query,
  recentlyUsedDSMap = {},
  showAppsmithAIQuery = false,
}: {
  allDatasources?: Datasource[];
  canCreateModules?: boolean;
  canCreateDatasource?: boolean;
  plugins?: Plugin[];
  query: string;
  recentlyUsedDSMap?: Record<string, number>;
  showAppsmithAIQuery?: boolean;
}) => {
  const fileOperations: ActionOperation[] = [];

  if (!canCreateModules) return fileOperations;

  const filteredActionOperations = actionOperations.filter(
    ({ title }) => title !== "New cURL import",
  );

  // Add appsmith AI operation if the feature flag is enabled
  const allActionOperations = showAppsmithAIQuery
    ? [...filteredActionOperations, appsmithAIActionOperation]
    : filteredActionOperations;

  /**
   *  Work around to get the rest api cloud image.
   *  We don't have it store as a svg
   */
  const actionOps = updateActionOperations(plugins, allActionOperations);

  // Add app datasources
  if (allDatasources.length > 0) {
    fileOperations.push(createQueryOption);
  }

  // Sort datasources based on recency
  const datasources = getSortedDatasources(allDatasources, recentlyUsedDSMap);

  const createQueryModuleGenerator =
    (datasourceId: string) => (packageId: string, from: EventLocation) =>
      createQueryModule({
        packageId,
        from,
        datasourceId,
        type: MODULE_TYPE.QUERY,
      });

  // map into operations
  const dsOperations = datasources.map((ds) =>
    generateCreateQueryForDSOption(ds, createQueryModuleGenerator(ds.id)),
  );

  fileOperations.push(...dsOperations);

  // Add generic action creation
  fileOperations.push(...actionOps);
  // Filter out based on query
  let filteredFileOperations = fileOperations
    .filter(Boolean)
    .filter((ds) => ds.title.toLowerCase().includes(query.toLowerCase()));
  // Add genetic datasource creation
  const onRedirect = () => {
    history.push(
      integrationEditorURL({
        selectedTab: INTEGRATION_TABS.NEW,
        generateEditorPath: true,
      }),
    );
  };

  if (canCreateDatasource)
    filteredFileOperations = generateCreateNewDSOption(
      filteredFileOperations,
      onRedirect,
    );

  return filteredFileOperations;
};
